import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Bot, PencilRuler, Calculator, Wallet, HardHat, Package, ShieldCheck,
  FileText, Clock, CreditCard, Users, CheckSquare, MessageSquare, ClipboardList,
  Wrench, Settings, Search, Bell, ChevronDown, LogOut, LayoutDashboard, Briefcase,
  UserSquare, ShoppingCart, Truck, BarChart3, Menu, X, Shield,
} from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const HOME_NAV = [
  ["Home", "/app/home", Home], ["AI Advisor", "/app/copilot", Bot], ["Studio", "/app/studio", PencilRuler],
  ["Projects", "/app/home", LayoutDashboard],
];
const BIZ_NAV = [
  ["Overview", "/app/business", LayoutDashboard], ["Leads", "/app/business/leads", UserSquare],
  ["Projects", "/app/business/projects", Briefcase], ["Marketplace", "/market", ShoppingCart],
  ["AI Copilot", "/app/copilot", Bot],
];

export function AppShell({ variant = "home", children, title }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = variant === "business" ? BIZ_NAV : HOME_NAV;

  useEffect(() => { api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {}); }, [loc.pathname]);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then(({ data }) => setResults(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const unread = notifs.filter((n) => !n.read).length;
  const markAll = () => { api.post("/notifications/read-all"); setNotifs(notifs.map((n) => ({ ...n, read: true }))); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-white/10 shrink-0">
        <Link to="/"><Logo dark /></Link>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 sp-scroll overflow-y-auto">
        {items.map(([label, to, Icon]) => {
          const active = loc.pathname === to;
          return (
            <Link key={label} to={to} onClick={() => setMobileOpen(false)} data-testid={`side-${label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${active ? "bg-sp-orange text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="h-4 w-4" />{label}
            </Link>
          );
        })}
        {user?.role === "admin" && (
          <Link to="/app/admin" onClick={() => setMobileOpen(false)} data-testid="side-admin"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${loc.pathname === "/app/admin" ? "bg-sp-orange text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <Shield className="h-4 w-4" />Admin
          </Link>
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 w-full"><LogOut className="h-4 w-4" />Sign out</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sp-light flex">
      <aside className="hidden lg:flex w-60 bg-sp-navy fixed inset-y-0 z-40">{SidebarInner}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <aside className="w-64 bg-sp-navy">{SidebarInner}</aside>
          <div className="flex-1 bg-black/50" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects, documents..." className="pl-9 h-9 bg-slate-50 border-slate-200" />
            {results.length > 0 && (
              <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50" data-testid="search-results">
                {results.map((r) => (
                  <button key={r.id} onClick={() => { nav(r.link); setQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between">
                    <div><div className="text-sm text-sp-navy">{r.title}</div><div className="text-xs text-slate-400">{r.subtitle}</div></div>
                    <span className="text-[10px] uppercase text-slate-400">{r.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 hidden sm:block text-sm font-medium text-sp-navy truncate">{title}</div>

          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notif-bell">
                <Bell className="h-5 w-5 text-slate-600" />
                {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-sp-orange text-[10px] text-white flex items-center justify-center">{unread}</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b"><span className="font-semibold text-sm">Notifications</span>{unread > 0 && <button onClick={markAll} className="text-xs text-sp-orange">Mark all read</button>}</div>
              <div className="max-h-80 overflow-y-auto sp-scroll">
                {notifs.length === 0 ? <div className="p-6 text-center text-sm text-slate-400">No notifications</div> :
                  notifs.slice(0, 12).map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b last:border-0 ${!n.read ? "bg-sp-orange/5" : ""}`}>
                      <div className="text-sm font-medium text-sp-navy">{n.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{n.message}</div>
                    </div>
                  ))}
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100" data-testid="profile-btn">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-sp-navy text-white text-xs">{(user?.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <div className="px-2 py-2 border-b mb-1"><div className="text-sm font-medium text-sp-navy">{user?.name}</div><div className="text-xs text-slate-400">{user?.email}</div></div>
              <button onClick={() => nav("/app/home")} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 flex items-center gap-2"><Home className="h-4 w-4" />My Home</button>
              <button onClick={() => nav("/app/business")} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 flex items-center gap-2"><Briefcase className="h-4 w-4" />My Business</button>
              <button onClick={logout} className="w-full text-left px-2 py-2 text-sm rounded hover:bg-slate-50 flex items-center gap-2 text-red-600"><LogOut className="h-4 w-4" />Sign out</button>
            </PopoverContent>
          </Popover>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
